import _ from 'lodash'
import { FiltersContextProvider } from './Filters/context'
import { useMemo, useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTable, usePagination } from 'react-table'
import * as Cells from './Cells'
import * as date from 'Utils/date'
import Filters, { parseFiltersFromURL, getInitialApiSearchParams } from './Filters'
import Pagination from 'Design/components/Pagination'
import ReactToolTip from 'Src/b/components/ReactTooltip/ReactTooltip'
import styles from './TrainingListWithAI.module.scss'
import Table, { TableActions, TableBody, TableHead, TableRow, Td, Th } from 'Design/components/Table'
import { useGetTrainingListWithAIQuery } from '../trainingWithAI.service'
import TrainingOptions from './Options'
import { NO_RESULTS_MSG } from 'Src/constants'
import EditTrainingModal from './EditModal'
import PageLoader from 'Layout/PageLoader'

const TrainingListWithAI = () => {
  const [trainingToEdit, setTrainingToEdit] = useState(null)
  const [, setSearchParams] = useSearchParams()

  const initialParams = useMemo(parseFiltersFromURL, [])
  const [queryParams, setQueryParams] = useState(getInitialApiSearchParams(initialParams))
  const [pageSize] = useState(initialParams.size)

  const [dateRangeFilter, setDateRangeFilter] = useState(initialParams.dateRange)
  const [botResponseFilter, setBotResponseFilter] = useState(initialParams.botResponse)
  const [adminFeedbackFilter, setAdminFeedbackFilter] = useState(initialParams.adminFeedback)
  const [visitorFeedbackFilter, setVisitorFeedbackFilter] = useState(initialParams.visitorFeedback)

  const { data = {}, isLoading } = useGetTrainingListWithAIQuery(queryParams)
  const { results: trainingListWithAI = [], meta: paginationDetails = {} } = data

  const debouncedSetQueryParams = useMemo(() => {
    return _.debounce(setQueryParams, 500, { leading: true, trailing: true })
  }, [])

  const columns = useMemo(() => {
    return [
      {
        Header: 'Visitor Question',
        accessor: 'data.question_asked_by_visitor'
      },
      {
        Header: 'Response Shown',
        accessor: 'data.response_shown_to_visitor',
        Cell: Cells.ResponseShownByBot
      },
      {
        Header: 'Visitor Feedback',
        accessor: 'data.visitor_feedback',
        Cell: Cells.VisitorFeedback
      },
      {
        Header: 'Content Feedback',
        accessor: 'data.admin_feedback',
        Cell: Cells.AdminFeedback
      },
      {
        Header: 'Created On',
        accessor: 'created_at',
        Cell: Cells.CreatedOn,
        Tooltip: ({ cell: { value } }) => (value ? date.formatLongDateTime(value) : null)
      }
    ]
  }, [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    gotoPage,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data: trainingListWithAI,
      initialState: {
        pageIndex: initialParams.page - 1,
        pageSize
      },
      manualPagination: true,
      pageCount: Math.ceil(paginationDetails.total / pageSize)
    },
    usePagination
  )

  useEffect(() => {
    const params = {}

    if (dateRangeFilter.length === 2) {
      params.start_date = dateRangeFilter[0]
      params.end_date = dateRangeFilter[1]
    }

    if (botResponseFilter) {
      params.prediction_outcome = botResponseFilter
    }

    if (adminFeedbackFilter) {
      params.admin_feedback = adminFeedbackFilter
    }

    if (visitorFeedbackFilter) {
      params.visitor_feedback = visitorFeedbackFilter
    }

    const urlParams = {
      page: pageIndex + 1,
      size: pageSize,
      ...params
    }

    const apiParams = {
      offset: pageIndex * pageSize,
      limit: pageSize,
      ...params
    }
    debouncedSetQueryParams(apiParams)
    setSearchParams(urlParams, { replace: true })
  }, [
    pageIndex,
    pageSize,
    setSearchParams,
    debouncedSetQueryParams,
    botResponseFilter,
    dateRangeFilter,
    adminFeedbackFilter,
    visitorFeedbackFilter
  ])

  const resetPage = useCallback(() => gotoPage(0), [gotoPage])

  const filterValue = {
    resetPage,
    dateRange: dateRangeFilter,
    setDateRange: setDateRangeFilter,
    adminFeedback: adminFeedbackFilter,
    setAdminFeedback: setAdminFeedbackFilter,
    visitorFeedback: visitorFeedbackFilter,
    setVisitorFeedback: setVisitorFeedbackFilter,
    botResponse: botResponseFilter,
    setBotResponse: setBotResponseFilter
  }

  if (isLoading) {
    return <PageLoader />
  }

  return (
    <div>
      <TableActions>
        <FiltersContextProvider value={filterValue}>
          <Filters />
        </FiltersContextProvider>
        <Pagination
          currentPage={pageIndex + 1}
          pageSize={pageSize}
          totalItems={paginationDetails.total}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableActions>
      <Table className={styles.Table} {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup) => {
            const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps()

            return (
              <TableRow key={key} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key, ...headerProps } = column.getHeaderProps()

                  return (
                    <Th key={key} {...headerProps}>
                      {column.render('Header')}
                    </Th>
                  )
                })}
                <Th isOption />
              </TableRow>
            )
          })}
        </TableHead>

        <TableBody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)
            const training = row.original
            const { key, ...rowProps } = row.getRowProps()

            return (
              <TableRow clickable key={key} {...rowProps}>
                {row.cells.map((cell) => {
                  const { key, ...cellProps } = cell.getCellProps()
                  const isDateColumn = key.includes('created_at')

                  return (
                    <Td key={key} {...cellProps} onClick={() => setTrainingToEdit(training)}>
                      {isDateColumn && (
                        <div className='date-cell-container'>
                          {/* Render cell value with the expected date format in tooltip */}
                          {cell.value && (
                            <ReactToolTip
                              className='bot-date-tooltip'
                              side='top'
                              content={
                                <div className='bot-date-tooltip-content' onClick={(e) => e.stopPropagation()}>
                                  {cell.render('Tooltip')}
                                </div>
                              }
                            >
                              {cell.render('Cell')}
                            </ReactToolTip>
                          )}
                          {/* Render '-' if cell has no value without tooltip */}
                          {!cell.value && cell.render('Cell')}
                        </div>
                      )}
                      {!isDateColumn && cell.render('Cell')}
                    </Td>
                  )
                })}
                <Td>
                  <TrainingOptions {...training} />
                </Td>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {trainingToEdit && <EditTrainingModal {...trainingToEdit} onHide={() => setTrainingToEdit(null)} />}
      {rows.length === 0 && <div className={styles.NoFilterResults}>{NO_RESULTS_MSG}</div>}
    </div>
  )
}

export default TrainingListWithAI
